/* ***********  Доработка отображения сертификатов   ********* */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Popover } from 'antd'
import { FilterOutlined } from '@ant-design/icons'

import { rqKeys } from '@/react-query-keys'
import { formatMoney, formatTs } from '@/helpers'
import { apiRequestNew } from '@/helpers/request'
import { E_CertificateTypes } from '@/enums'
import {
  removePendingArchiveQueueItem,
} from '@/helpers/certificate-archive-offline-queue'
import {
  removePendingRestoreQueueItem,
  upsertPendingRestoreQueueItem,
} from '@/helpers/certificate-restore-offline-queue'
import { useCertificatesRest } from '@/helpers/query/use-certificates-rest'
import { useAppStore } from '@/store'
import { Drawer, Flex, Modal } from '@/components'
import { OrganizationBanner } from '@/b-components/organization-banner'
import type {
  T_CertificateNominalsListRes,
  T_CertificatesListRes,
} from '@/types/responses'
import { useArchiveCertificate } from '../use-archive-certificate'
import trashSvg from '@/img/icon-trash.svg'
import fakeQRPng from '@/img/fake-qr.png?inline'
import logoPyaterochaBuyCertPng from '@/img/logo-pyaterochka-buy-cert.png?inline'
import logoMagnitBuyCertPng from '@/img/logo-magnit-buy-cert.png?inline'
import logoVkusvillBuyCertPng from '@/img/logo-vkusvill-buy-cert.png?inline'
import {
  SC_CertificatesList,
  SC_CertificateWrapper,
  SC_LeftSide,
  SC_RightSide,
  SC_Denomination,
  SC_DeleteCertificate,
  SC_QR,
  SC_ButtonWrapper,
  SC_BackgroundBlur,
  SC_Button,
} from './styled'


interface T_ExistCertificatesDrawerProps {
  certificatesList: T_CertificatesListRes
  archivedCertificatesList: T_CertificatesListRes
  hideExistCertificatesDrawer: () => void
  setSelectedCertificateId: Dispatch<SetStateAction<number | undefined>>
  setSelectedCertificateType: Dispatch<SetStateAction<E_CertificateTypes | null>>
  showCertificateDetailsDrawer: () => void
  isArchivedCertificatesMode: boolean
  setCertificatesListToArchivedMode: () => void
  setCertificatesListToRegularMode: () => void
}

export const ExistCertificatesDrawer: FC<T_ExistCertificatesDrawerProps> = ({
  certificatesList,
  archivedCertificatesList,
  hideExistCertificatesDrawer,
  setSelectedCertificateId,
  setSelectedCertificateType,
  showCertificateDetailsDrawer,
  isArchivedCertificatesMode,
  setCertificatesListToArchivedMode,
  setCertificatesListToRegularMode,
}) => {
  const queryClient = useQueryClient()
  const logCertListFlow = (_phase: string, _payload: Record<string, unknown> = {}) => {
    void _phase
    void _payload
  }
  const ARCHIVED_PAGE_SIZE = 10
  const NOMINAL_CATEGORY_MAP_LS_KEY = 'ERA_CERT_NOMINAL_CATEGORY_MAP'

  const userData = useAppStore((s) => s.userData)
  const certificateCategoriesList = useAppStore((s) => s.certificateCategoriesList)
  const certificateNominalsList = useAppStore((s) => s.certificateNominalsList)
  const { data: certificatesRestList } = useCertificatesRest(userData.id || undefined)
  const [ selectedRegularStoreId, setSelectedRegularStoreId ] = useState<number | null>(null)
  const [ selectedArchivedStoreId, setSelectedArchivedStoreId ] = useState<number | null>(null)
  const [ isFilterPopoverOpen, setIsFilterPopoverOpen ] = useState(false)
  const activeSelectedStoreId = isArchivedCertificatesMode
    ? selectedArchivedStoreId
    : selectedRegularStoreId

  const [ archivedVisibleList, setArchivedVisibleList ] = useState<T_CertificatesListRes>([])
  const [ archivedHasMore, setArchivedHasMore ] = useState(true)
  const archivedOffsetRef = useRef(0)
  const archivedHasMoreRef = useRef(true)
  const isArchivedLoadingMoreRef = useRef(false)
  const archivedSourceListRef = useRef<T_CertificatesListRes>([])
  const archivedLoadMoreRafIdRef = useRef<number | null>(null)
  const isArchivedLoadMoreRafScheduledRef = useRef(false)
  const lastArchivedLoadMoreScheduledAtRef = useRef(0)
  const drawerScrollRootRef = useRef<HTMLDivElement | null>(null)
  const persistedNominalCategoryMapRef = useRef<Record<string, number>>({})
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null)

  const storeOptions = useMemo(() => (
    (certificateCategoriesList || [])
      .filter((c) => !!c.parent)
      .sort((a, b) => (a.title?.ru || '').localeCompare(b.title?.ru || ''))
  ), [ certificateCategoriesList ])

  const getStoreLogoByName = useCallback((name?: string | null) => {
    if (name === E_CertificateTypes.Magnit) return logoMagnitBuyCertPng
    if (name === E_CertificateTypes.Pyaterochka) return logoPyaterochaBuyCertPng
    if (name === E_CertificateTypes.Vkusvill) return logoVkusvillBuyCertPng
    return null
  }, [])

  const selectedStoreIcon = useMemo(() => {
    if (!activeSelectedStoreId) return null
    const selectedStore = storeOptions.find((s) => s.id === activeSelectedStoreId)
    return getStoreLogoByName(selectedStore?.name)
  }, [ activeSelectedStoreId, getStoreLogoByName, storeOptions ])

  const nominalCategoryMap = useMemo(() => {
    const map: Record<string, number> = {}
    certificateNominalsList.forEach((cN) => {
      cN.list.forEach((nominal) => {
        if (Number.isFinite(Number(nominal?.id)) && Number.isFinite(Number(nominal?.category))) {
          map[String(nominal.id)] = Number(nominal.category)
        }
      })
    })
    return map
  }, [ certificateNominalsList ])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(NOMINAL_CATEGORY_MAP_LS_KEY)
      const parsed = raw ? JSON.parse(raw) : {}
      persistedNominalCategoryMapRef.current = (parsed && typeof parsed === 'object') ? parsed : {}
    } catch {
      persistedNominalCategoryMapRef.current = {}
    }
  }, [])

  useEffect(() => {
    if (!Object.keys(nominalCategoryMap).length) return
    persistedNominalCategoryMapRef.current = {
      ...persistedNominalCategoryMapRef.current,
      ...nominalCategoryMap,
    }
    try {
      localStorage.setItem(
        NOMINAL_CATEGORY_MAP_LS_KEY,
        JSON.stringify(persistedNominalCategoryMapRef.current)
      )
    } catch {
      // ignore
    }
  }, [ nominalCategoryMap ])

  const getNominalCategoryId = useCallback((nominalId: number) => {
    const byCurrent = nominalCategoryMap[String(nominalId)]
    if (Number.isFinite(byCurrent)) return byCurrent

    const byPersisted = Number(persistedNominalCategoryMapRef.current[String(nominalId)])
    if (Number.isFinite(byPersisted)) return byPersisted

    return undefined
  }, [ nominalCategoryMap ])

  const getCertificateType = (nominalId: number) => {
    const foundCategoryId = getNominalCategoryId(nominalId)

    if (!foundCategoryId) return null

    const foundCertificateCategory = certificateCategoriesList.find((c) => (
      c.id === foundCategoryId
    ))

    if (!foundCertificateCategory) return null

    return foundCertificateCategory.name
  }

  const getNominalValue = (nominalId: number) => (
    certificateNominalsList
      .reduce((acc: T_CertificateNominalsListRes, cN) => ([
        ...acc,
        ...(cN.list),
      ]), [])
      .find((cN) => cN.id === nominalId)?.price.base.rur || 0
  )

  const normalizeCertTimeMs = useCallback((t: unknown) => {
    const n = Number(t)
    if (!Number.isFinite(n) || n <= 0) return 0
    return n < 1e12 ? n * 1000 : n
  }, [])

  const sortCertificatesForUi = useCallback((list: T_CertificatesListRes) => (
    [ ...(list || []) ].sort((a, b) => {
      const aTime = normalizeCertTimeMs(a.time)
      const bTime = normalizeCertTimeMs(b.time)
      if (aTime !== bTime) return bTime - aTime
      return Number(b.id) - Number(a.id)
    })
  ), [ normalizeCertTimeMs ])

  const openCertificateDetailsDrawer = (certificateId: number, goodsId: number) => {
    setSelectedCertificateId(certificateId)
    setSelectedCertificateType(getCertificateType(goodsId) as E_CertificateTypes | null)
    showCertificateDetailsDrawer()
  }

  const [ certificateIdToArchive, setCertificateIdToArchive ] = useState<number>()
  const [ certificateIdToRestore, setCertificateIdToRestore ] = useState<number>()

  const moveCertificateToArchive = (certificateId: number) => {
    logCertListFlow('open-archive-confirm', { certificateId: Number(certificateId) })
    setCertificateIdToArchive(certificateId)
  }

  const restoreCertificateFromArchive = (certificateId: number) => {
    logCertListFlow('open-restore-confirm', { certificateId: Number(certificateId) })
    setCertificateIdToRestore(certificateId)
  }

  const applyOptimisticRestoreMove = (certificateIdToRestore: number) => {
    logCertListFlow('apply-restore-optimistic:start', { certificateId: Number(certificateIdToRestore) })
    const previousCertificates = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList ])
    const previousArchived = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList, 'archived' ])
    const certificateToRestore = previousArchived?.find((c) => c.id === certificateIdToRestore)
    if (!certificateToRestore) return { previousCertificates, previousArchived }

    removePendingArchiveQueueItem(userData.id, Number(certificateIdToRestore))
    upsertPendingRestoreQueueItem(userData.id, {
      cert: certificateToRestore,
      movedAt: Date.now(),
    })

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => {
        const next = [ certificateToRestore, ...(old || []) ]
          .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
        return next
      }
    )
    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList, 'archived' ],
      (old) => (old || []).filter((c) => c.id !== Number(certificateIdToRestore))
    )

    logCertListFlow('apply-restore-optimistic:done', {
      certificateId: Number(certificateIdToRestore),
      foundInArchived: Boolean(certificateToRestore),
      activeCountBefore: previousCertificates?.length || 0,
      archivedCountBefore: previousArchived?.length || 0,
    })
    return { previousCertificates, previousArchived }
  }

  const restoreWithOfflineQueue = async (id: number) => {
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    logCertListFlow('restore:start', { certificateId: Number(id), isOffline })
    if (isOffline) {
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      applyOptimisticRestoreMove(id)
      setCertificateIdToRestore(undefined)
      logCertListFlow('restore:offline-optimistic-applied', { certificateId: Number(id) })
      return
    }

    restoreCertificateMutation(id)
    logCertListFlow('restore:online-mutate-dispatched', { certificateId: Number(id) })
  }

  const {
    archiveWithOfflineQueue: archiveWithOfflineQueueBase,
    isMoveCertificateToArchivePending,
  } = useArchiveCertificate()
  const archiveWithOfflineQueue = async (id: number) => {
    const fallbackCert = (certificatesList || []).find((c) => c.id === id)
    await archiveWithOfflineQueueBase(id, fallbackCert)
    setCertificateIdToArchive(undefined)
  }

  const {
    mutate: restoreCertificateMutation,
    isPending: isRestoreCertificatePending,
  } = useMutation({
    mutationFn: (certificateIdToRestore: number) => apiRequestNew<any>(
      'GET',
      `/era/gift/certificate/${certificateIdToRestore}/restore`,
    ),
    onMutate: (certificateIdToRestore) => {
      logCertListFlow('restore:mutate', { certificateId: Number(certificateIdToRestore) })
      const context = applyOptimisticRestoreMove(certificateIdToRestore)
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      return context
    },
    onSuccess: (_res, certificateIdToRestore) => {
      logCertListFlow('restore:success', { certificateId: Number(certificateIdToRestore) })
      removePendingArchiveQueueItem(userData.id, Number(certificateIdToRestore))
    },
    onError: (err: any, certificateIdToRestore, context: any) => {
      const isOfflineNow = typeof navigator !== 'undefined' && navigator.onLine === false
      const isNetworkError = isOfflineNow || err?.code === 'ERR_NETWORK' || !err?.response
      const isAlreadyRestored = Number(err?.response?.status) === 404
      logCertListFlow('restore:error', {
        certificateId: Number(certificateIdToRestore),
        code: err?.code,
        status: Number(err?.response?.status) || undefined,
        isNetworkError,
        isAlreadyRestored,
      })
      if (isNetworkError) {
        return
      }
      if (isAlreadyRestored) {
        removePendingArchiveQueueItem(userData.id, Number(certificateIdToRestore))
        return
      }

      removePendingRestoreQueueItem(userData.id, Number(certificateIdToRestore))
      if (context?.previousCertificates) {
        queryClient.setQueryData([ rqKeys.certificatesList ], context.previousCertificates)
      }
      if (context?.previousArchived) {
        queryClient.setQueryData([ rqKeys.certificatesList, 'archived' ], context.previousArchived)
      }
    },
    onSettled: () => {
      logCertListFlow('restore:settled')
      setCertificateIdToRestore(undefined)
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList ], refetchType: 'none' })
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], refetchType: 'none' })
    },
  })

  const archivedSourceList = useMemo(() => {
    const base = !selectedArchivedStoreId
      ? (archivedCertificatesList || [])
      : (archivedCertificatesList || []).filter((c) => (
        getNominalCategoryId(c.goods) === selectedArchivedStoreId
      ))
    return sortCertificatesForUi(base)
  }, [ archivedCertificatesList, getNominalCategoryId, selectedArchivedStoreId, sortCertificatesForUi ])

  useEffect(() => {
    archivedSourceListRef.current = archivedSourceList
  }, [ archivedSourceList ])

  const fetchArchivedPage = useCallback((opts: { reset: boolean }) => {
    if (isArchivedLoadingMoreRef.current) return
    if (!opts.reset && !archivedHasMoreRef.current) return

    isArchivedLoadingMoreRef.current = true
    const source = archivedSourceListRef.current || []
    const nextOffset = opts.reset ? 0 : archivedOffsetRef.current
    const batch = source.slice(nextOffset, nextOffset + ARCHIVED_PAGE_SIZE)

    if (opts.reset) {
      setArchivedVisibleList(batch)
    } else {
      setArchivedVisibleList((old) => [ ...(old || []), ...batch ])
    }

    const newOffset = nextOffset + batch.length
    const hasMore = newOffset < source.length
    archivedOffsetRef.current = newOffset
    setArchivedHasMore(hasMore)
    archivedHasMoreRef.current = hasMore
    isArchivedLoadingMoreRef.current = false
  }, [
    ARCHIVED_PAGE_SIZE,
  ])

  const scheduleArchivedLoadMore = useCallback(() => {
    if (isArchivedLoadMoreRafScheduledRef.current) return

    // Soft rate-limit: prevents load-more burst on very fast iOS bounce scroll.
    const now = Date.now()
    if (now - lastArchivedLoadMoreScheduledAtRef.current < 120) return
    lastArchivedLoadMoreScheduledAtRef.current = now

    isArchivedLoadMoreRafScheduledRef.current = true

    archivedLoadMoreRafIdRef.current = window.requestAnimationFrame(() => {
      isArchivedLoadMoreRafScheduledRef.current = false
      archivedLoadMoreRafIdRef.current = null
      fetchArchivedPage({ reset: false })
    })
  }, [ fetchArchivedPage ])

  useEffect(() => {
    if (!isArchivedCertificatesMode) return
    setArchivedVisibleList([])
    setArchivedHasMore(true)
    archivedOffsetRef.current = 0
    archivedHasMoreRef.current = true
    fetchArchivedPage({ reset: true })
  }, [ archivedSourceList, isArchivedCertificatesMode, selectedArchivedStoreId, fetchArchivedPage ])

  useEffect(() => {
    if (!isArchivedCertificatesMode) return
    if (!archivedHasMore) return

    const target = loadMoreTriggerRef.current
    if (!target) return

    let observer: IntersectionObserver | null = null
    let waitRootRafId: number | null = null
    let scrollRoot: HTMLDivElement | null = null

    const tryLoadMoreNearBottom = () => {
      if (!scrollRoot) return
      const remaining = scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight
      if (remaining <= 260) {
        scheduleArchivedLoadMore()
      }
    }

    const mountObserver = () => {
      const root = drawerScrollRootRef.current
      if (!root) {
        waitRootRafId = window.requestAnimationFrame(mountObserver)
        return
      }
      scrollRoot = root

      observer = new IntersectionObserver((entries) => {
        const [ first ] = entries
        if (!first?.isIntersecting) return
        scheduleArchivedLoadMore()
      }, {
        root,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0.01,
      })

      observer.observe(target)
      scrollRoot.addEventListener('scroll', tryLoadMoreNearBottom, { passive: true })
      tryLoadMoreNearBottom()
    }

    mountObserver()
    return () => {
      if (observer) observer.disconnect()
      if (scrollRoot) {
        scrollRoot.removeEventListener('scroll', tryLoadMoreNearBottom)
        scrollRoot = null
      }
      if (waitRootRafId !== null) {
        window.cancelAnimationFrame(waitRootRafId)
        waitRootRafId = null
      }
      if (archivedLoadMoreRafIdRef.current !== null) {
        window.cancelAnimationFrame(archivedLoadMoreRafIdRef.current)
        archivedLoadMoreRafIdRef.current = null
      }
      isArchivedLoadMoreRafScheduledRef.current = false
    }
  }, [ archivedHasMore, isArchivedCertificatesMode, scheduleArchivedLoadMore ])

  const restMap = useMemo(() => {
    if (!certificatesRestList) return {}
    const map: Record<string, { value: number, ts: number }> = {}

    certificatesRestList.forEach((item) => {
      map[`${item.certificateId}-${item.nominalId}`] = {
        value: Number(item.amount),
        ts: Number(item.updatedAtTs || 0),
      }
    })

    return map
  }, [ certificatesRestList ])

  const visibleRegularList = selectedRegularStoreId
    ? (certificatesList || []).filter((c) => getNominalCategoryId(c.goods) === selectedRegularStoreId)
    : certificatesList

  const filterPopoverContent = (
    <div style={{ minWidth: 180 }}>
      <div
        onClick={() => {
          if (isArchivedCertificatesMode) {
            setSelectedArchivedStoreId(null)
          } else {
            setSelectedRegularStoreId(null)
          }
          setIsFilterPopoverOpen(false)
        }}
        style={{
          cursor: 'pointer',
          padding: '8px 10px',
          borderRadius: 8,
          background: activeSelectedStoreId === null ? '#f2f2f2' : 'transparent',
          marginBottom: 4,
          fontSize: 14,
        }}
      >
        Все магазины
      </div>

      {storeOptions.map((store) => (
        <div
          key={store.id}
          onClick={() => {
            if (isArchivedCertificatesMode) {
              setSelectedArchivedStoreId(store.id)
            } else {
              setSelectedRegularStoreId(store.id)
            }
            setIsFilterPopoverOpen(false)
          }}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 8,
            background: activeSelectedStoreId === store.id ? '#f2f2f2' : 'transparent',
            marginBottom: 4,
          }}
        >
          <img
            src={getStoreLogoByName(store.name) || logoVkusvillBuyCertPng}
            alt=''
            style={{ width: 24, height: 24, borderRadius: '50%' }}
          />
          <span style={{ fontSize: 14 }}>{store.title?.ru || 'Неизвестно'}</span>
        </div>
      ))}
    </div>
  )


  return (
    <Drawer
      scrollRootRef={drawerScrollRootRef}
      backTitle={
        isArchivedCertificatesMode
          ? 'Архивные сертификаты'
          : 'Продуктовые сертификаты'
      }
      onClose={() => {
        if (isArchivedCertificatesMode) setCertificatesListToRegularMode()
        hideExistCertificatesDrawer()
      }}
      onBackClick={
        isArchivedCertificatesMode
          ? setCertificatesListToRegularMode
          : hideExistCertificatesDrawer
      }
      headerRight={
        <Popover
          trigger='click'
          content={filterPopoverContent}
          placement='bottomRight'
          open={isFilterPopoverOpen}
          onOpenChange={setIsFilterPopoverOpen}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 10, cursor: 'pointer' }}>
            {selectedStoreIcon && (
              <img
                src={selectedStoreIcon}
                alt=''
                style={{ width: 18, height: 18, borderRadius: '50%' }}
              />
            )}

            <FilterOutlined style={{
              fontSize: 18,
              color: activeSelectedStoreId ? '#1677ff' : '#595959',
              cursor: 'pointer',
            }}
            />
          </div>
        </Popover>
      }
      isBackdropClosable
      isFullScreen
    >
      <SC_CertificatesList>
        {(
          isArchivedCertificatesMode
            ? archivedVisibleList
            : visibleRegularList
        )?.length === 0 ? (
            <Flex justify='center'>
              <h2>
                {isArchivedCertificatesMode ? 'Архив пуст' : 'Сертификаты не найдены'}
              </h2>
            </Flex>
          ) : (
            (isArchivedCertificatesMode && archivedVisibleList
              ? archivedVisibleList
              : visibleRegularList
            ).map((c) => (
              <SC_CertificateWrapper
                key={c.id}
                onClick={c.id > 0 ? () => openCertificateDetailsDrawer(c.id, c.goods) : undefined}
              >
                <SC_LeftSide>
                  <OrganizationBanner
                    certificateId={c.id}
                    certificateType={getCertificateType(c.goods)}
                  />

                  {(() => {
                    const key = `${c.id}-${c.goods}`
                    const restEntry = restMap[key]
                    const rest = restEntry?.value
                    const ts = restEntry?.ts
                    const nominal = getNominalValue(c.goods)

                    return (
                      <SC_Denomination>
                        {rest !== undefined ? (
                          <span>
                            {`${
                              formatMoney(Math.round(rest), false, true)
                            } из ${
                              formatMoney(Math.round(nominal / 100))
                            }`}
                          </span>
                        ) : (
                          <span>{formatMoney(nominal, true)}</span>
                        )}

                        <span style={{ whiteSpace: 'nowrap' }}>
                          {rest === undefined ? 'Номинал' : `Доступно ${ts ? ` на ${formatTs(ts)}` : ''}`}
                        </span>
                      </SC_Denomination>
                    )
                  })()}
                </SC_LeftSide>

                <SC_RightSide>
                  {!isArchivedCertificatesMode && (
                    <SC_DeleteCertificate
                      onClick={(e) => {
                        e.stopPropagation()
                        if (c.id <= 0) return
                        moveCertificateToArchive(c.id)
                      }}
                      style={c.id <= 0 ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                    >
                      <span>В архив</span>
                      <img src={trashSvg} />
                    </SC_DeleteCertificate>
                  )}
                  {isArchivedCertificatesMode && (
                    <SC_DeleteCertificate
                      onClick={(e) => {
                        e.stopPropagation()
                        if (c.id <= 0) return
                        restoreCertificateFromArchive(c.id)
                      }}
                      style={c.id <= 0 ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                    >
                      <span style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginLeft: 'auto',
                        transform: 'translateX(8px)',
                        lineHeight: '1.15',
                      }}
                      >
                        <span>Восстановить</span>
                        <span>из архива</span>
                      </span>
                    </SC_DeleteCertificate>
                  )}

                  <SC_QR src={fakeQRPng} />
                </SC_RightSide>
              </SC_CertificateWrapper>
            ))
          )
        }
        {isArchivedCertificatesMode && archivedHasMore && (
          <div ref={loadMoreTriggerRef} style={{ height: 1 }} />
        )}
      </SC_CertificatesList>

      <SC_ButtonWrapper>
        <SC_BackgroundBlur />

        {!isArchivedCertificatesMode ? (
          <SC_Button type='primary' onClick={setCertificatesListToArchivedMode}>
            Архивные сертификаты
          </SC_Button>
        ) : (
          <SC_Button type='primary' onClick={setCertificatesListToRegularMode}>
            Назад
          </SC_Button>
        )}
      </SC_ButtonWrapper>

      {!!certificateIdToArchive && (
        <Modal
          title='Вы уверены, что хотите архивировать сертификат?'
          open={!!certificateIdToArchive}
          onOk={() => {
            void archiveWithOfflineQueue(certificateIdToArchive)
          }}
          onCancel={() => setCertificateIdToArchive(undefined)}
          okText='Архивировать'
          cancelText='Отмена'
          okButtonProps={{ loading: isMoveCertificateToArchivePending }}
        />
      )}

      {!!certificateIdToRestore && (
        <Modal
          title='Вы уверены, что хотите восстановить сертификат из архива?'
          open={!!certificateIdToRestore}
          onOk={() => {
            void restoreWithOfflineQueue(certificateIdToRestore)
          }}
          onCancel={() => setCertificateIdToRestore(undefined)}
          okText='Восстановить'
          cancelText='Отмена'
          okButtonProps={{ loading: isRestoreCertificatePending }}
        />
      )}
    </Drawer>
  )
}
